import React from 'react';
import moment from 'moment';
import { Avatar, Badge, Box, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IConversation } from '~/models';

// ─── Styles that DS cannot express ───────────────────────────────────────────
// DS Row/Column only expose padding props (pt/pb/px/py), not margin.
// The outer 40px margin must remain inline until the parent layout owns the spacing.
const LIST_WRAPPER_STYLE = { margin: 40 } as const;

// The divider is rendered between items (not on the item itself) so the last
// row has no trailing border. Box has no border shorthand prop.
const DIVIDER_STYLE = { height: 1, backgroundColor: '#ededed' } as const;

// DS Row has no cursor prop.
const ROW_ITEM_STYLE = { cursor: 'pointer' } as const;

// Badge dot size="sm" handles the shape (rounded-full, 8×8px).
// Two properties remain that Badge can't express:
//   backgroundColor — Badge has no 'secondary' variant (only primary/success/warning/error/neutral)
//   margin         — 12px side gutter; can't use Row gap because the dot spacing (12px) differs
//                    from the avatar-to-text spacing (20px), so a single gap value can't satisfy both.
const UNREAD_DOT_STYLE = {
  backgroundColor: colors.secondary[500],
  margin: '0 12px',
} as const;

// The text column must fill remaining row space (flex:1) and sit 20px right of
// the avatar. marginLeft can't be a Column DS prop (pl adds internal padding, not
// external spacing), and Expanded only handles flex:1 — both are needed together.
const TEXT_COLUMN_STYLE = { marginLeft: 20, flex: 1 } as const;


// wordBreak and whiteSpace have no DS equivalents — required to prevent long
// message strings from overflowing their container.
const LAST_MESSAGE_STYLE = {
  wordBreak: 'break-word' as const,
  whiteSpace: 'pre-wrap' as const,
};

// Row justify="center" handles display:flex + justifyContent.
// width:85 (fixed pixel) and SVG color (inherited via CSS 'color') have no Row props.
const ARROW_CONTAINER_STYLE = {
  width: 85,
  color: colors.secondary[500],
} as const;
// ─────────────────────────────────────────────────────────────────────────────

const isReadMessage = (item: IConversation) =>
  new Date(item.last_message.created_at) < new Date(item.user_last_read);

interface IMessageListItemProps {
  job: IConversation;
  onSelect: (job: IConversation) => void;
}

function MessageListItem({ job, onSelect }: IMessageListItemProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(job);
    }
  };

  return (
    <Row
      align="center"
      justify="between"
      py={3}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(job)}
      onKeyDown={handleKeyDown}
      style={ROW_ITEM_STYLE}
    >
      <Badge
        dot
        size="sm"
        data-testid="unread-dot"
        style={{ ...UNREAD_DOT_STYLE, opacity: isReadMessage(job) ? 0 : 1 }}
      />

      <Avatar
        size="lg"
        src={job.opponent_user.profile_image || undefined}
        alt="profile image"
      />

      <Column style={TEXT_COLUMN_STYLE} gap={2}>
        <Row align="center" gap={2}>
          <Typography size="body_large" bold>
            {`${job.opponent_user.first_name} ${job.opponent_user.last_name}`}
          </Typography>
          <Typography size="body_large" bold>|</Typography>
          <Typography size="body_large" bold color={colors.primary[500]}>
            {job.main_user.office_name}
          </Typography>
        </Row>

        <Typography size="body_large" style={LAST_MESSAGE_STYLE}>
          {job.last_message.text}
        </Typography>

        <Typography size="caption_sm" color={colors.neutral[400]}>
          {job?.isOnsiteInterview ? 'Interview' : 'Shift'} Date:{' '}
          {moment(new Date(job.job_datetime)).format('ddd, MMM DD, YYYY')}
        </Typography>
      </Column>

      <Row justify="center" style={ARROW_CONTAINER_STYLE}>
        <OutlineChevronRightIcon />
      </Row>
    </Row>
  );
}

interface IProps {
  selectedJobs: (job: IConversation) => void;
  isLoading: boolean;
}

export default function Messages({ selectedJobs, isLoading }: IProps) {
  const allMessages = useAppSelector((state) => state.messages.allJobsWithMessages);

  if (!isLoading && allMessages.length === 0) {
    return (
      <Column width="100%" py={10} px={5} align="center">
        <Typography size="body_large" textAlign="center" color={colors.neutral[500]}>
          You have no chats.
        </Typography>
      </Column>
    );
  }

  return (
    <Column width="90%" style={LIST_WRAPPER_STYLE}>
      {allMessages.map((job, index) => (
        <React.Fragment key={job.id}>
          {index > 0 && <Box style={DIVIDER_STYLE} />}
          <MessageListItem job={job} onSelect={selectedJobs} />
        </React.Fragment>
      ))}
    </Column>
  );
}
